import { useEffect } from 'react';

/**
 * Per-device "graphs vs food-list" column split for the food-log views
 * (Logs v2/v3). Same persistence story as the text-size / thumbnail prefs:
 * a localStorage value drives a CSS variable (`--foodlog-left`, the left
 * column's % width) on `<html>`, so dragging the divider on any day resizes
 * every day on both views at once — no React state threading.
 *
 * Until the clinician drags, `--foodlog-left` is unset and each view falls
 * back to its own `--foodlog-default-left` (see `.foodlog-grid` in index.css).
 */

const STORAGE_KEY = 'ui.foodLogSplit';
const VAR = '--foodlog-left';

/** Clamp so neither column collapses. */
export const FOODLOG_SPLIT_MIN = 25;
export const FOODLOG_SPLIT_MAX = 75;

const clamp = (pct: number) =>
  Math.min(FOODLOG_SPLIT_MAX, Math.max(FOODLOG_SPLIT_MIN, pct));

/** Live-apply the left-column width (called on every drag move). */
export const applyFoodLogSplit = (pct: number) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(VAR, `${clamp(pct)}%`);
};

/** Persist the chosen split (called on drag end). */
export const persistFoodLogSplit = (pct: number) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(clamp(pct)));
  } catch {
    /* private mode — ignore */
  }
};

const readStored = (): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) ? clamp(n) : null;
  } catch {
    return null;
  }
};

/** Apply the stored split (if any) on mount — before the food log paints. */
export const useApplyStoredFoodLogSplit = (): void => {
  useEffect(() => {
    const v = readStored();
    if (v != null) applyFoodLogSplit(v);
  }, []);
};
